import type { FieldErrors } from 'react-hook-form'
import type { z } from 'zod'
import {
  camposDaClassificacao,
  camposDoPlano,
  esquemaDaClassificacao,
  esquemaDoPlano,
  esquemaNovaGmud,
  type FormularioNovaGmud,
} from './esquema'

/** Nome do parâmetro na URL. Uma constante porque quem lê e quem escreve são arquivos diferentes. */
export const PARAMETRO_DA_ETAPA = 'etapa'

export type IdDeEtapa = 'classificacao' | 'plano' | 'conferencia'

export interface Etapa {
  id: IdDeEtapa
  /** Posição na URL (`?etapa=2`) e no indicador. Começa em 1: ninguém conta etapa a partir do zero. */
  numero: number
  rotulo: string
  /** Uma linha dizendo o que se faz aqui, para quem nunca abriu uma solicitação. */
  resumo: string
  /** Os campos que ESTA etapa contém — é o que leva o erro de volta para a tela onde ele mora. */
  campos: readonly (keyof FormularioNovaGmud)[]
  /** O que precisa estar válido para sair desta etapa. */
  esquema: z.ZodTypeAny
}

const CAMPOS_DA_ETAPA_1 = Object.keys(camposDaClassificacao.shape) as (keyof FormularioNovaGmud)[]
const CAMPOS_DA_ETAPA_2 = Object.keys(camposDoPlano.shape) as (keyof FormularioNovaGmud)[]

/**
 * As três etapas do cadastro, e nada além disso.
 *
 * O assistente de sete passos espelhando o ANEXO XXI é da Fase 2. Anunciar sete e entregar três
 * seria pior do que anunciar três: quem preenche organiza o próprio tempo pelo que a barra promete.
 *
 * A etapa 3 não se chama "Conferência e envio". "Enviar" tem um significado só neste sistema — ir
 * ao comitê —, e é justamente a confusão que se quer matar: no sistema anterior as pessoas
 * salvavam o cadastro e iam embora achando que o comitê já tinha a mudança na mão.
 */
export const ETAPA_DA_CLASSIFICACAO: Etapa = {
  id: 'classificacao',
  numero: 1,
  rotulo: 'Classificação',
  resumo: 'De onde vem a mudança, o que ela afeta e quem responde por ela.',
  campos: CAMPOS_DA_ETAPA_1,
  esquema: esquemaDaClassificacao,
}

export const ETAPA_DO_PLANO: Etapa = {
  id: 'plano',
  numero: 2,
  rotulo: 'Plano da mudança',
  resumo: 'Como a mudança será feita e como se volta atrás se algo der errado.',
  campos: CAMPOS_DA_ETAPA_2,
  esquema: esquemaDoPlano,
}

export const ETAPA_DA_CONFERENCIA: Etapa = {
  id: 'conferencia',
  numero: 3,
  rotulo: 'Conferência',
  resumo: 'Releia o que preencheu e salve o rascunho.',
  campos: [],
  // A última etapa confere o formulário inteiro: é a única que grava, e é onde um campo da
  // etapa 1 apagado depois de validado apareceria de novo.
  esquema: esquemaNovaGmud,
}

export const ETAPAS: readonly Etapa[] = [ETAPA_DA_CLASSIFICACAO, ETAPA_DO_PLANO, ETAPA_DA_CONFERENCIA]

/**
 * A etapa seguinte e a anterior.
 *
 * Devolvem `undefined` nas pontas em vez de dar a volta: é o que faz a tela saber que está na
 * última — e é ali que o botão deixa de ser "Continuar" e passa a gravar.
 *
 * @param etapa a etapa corrente
 * @returns a vizinha, ou indefinido se não há
 */
export function proximaEtapa(etapa: Etapa): Etapa | undefined {
  return ETAPAS.find((outra) => outra.numero === etapa.numero + 1)
}

/** @see proximaEtapa */
export function etapaAnterior(etapa: Etapa): Etapa | undefined {
  return ETAPAS.find((outra) => outra.numero === etapa.numero - 1)
}

/**
 * Qual etapa a URL está pedindo.
 *
 * Tudo que não é uma das três vira a primeira, sem erro na tela: `?etapa=9` e `?etapa=abc` chegam
 * de link velho colado em e-mail, e recusar o endereço ensinaria que o sistema quebrou quando o
 * certo é começar do começo.
 *
 * @param parametros a query string corrente
 * @returns a etapa correspondente
 */
export function lerEtapa(parametros: URLSearchParams): Etapa {
  const numero = Number(parametros.get(PARAMETRO_DA_ETAPA))
  return ETAPAS.find((etapa) => etapa.numero === numero) ?? ETAPA_DA_CLASSIFICACAO
}

/**
 * A etapa em que um campo é preenchido.
 *
 * @param campo nome do campo no formulário
 * @returns a etapa dona dele, ou indefinido se o campo não é de etapa nenhuma
 */
export function etapaDoCampo(campo: string): Etapa | undefined {
  return ETAPAS.find((etapa) => (etapa.campos as readonly string[]).includes(campo))
}

/**
 * A primeira etapa que tem erro, na ordem em que o usuário as percorreu.
 *
 * Sem isto, salvar na etapa 3 com um campo inválido na etapa 1 mostra "corrija os erros" apontando
 * para campos que não estão na tela — a pessoa lê a mensagem, olha o formulário inteiro na frente
 * dela e não acha o que corrigir. É o caso de quem recarrega a página em `?etapa=3`: o formulário
 * volta vazio e o erro está lá atrás.
 *
 * @param erros o que o resolver reprovou
 * @returns a etapa para onde levar o usuário, ou indefinido se nenhum erro é de campo
 */
export function primeiraEtapaComErro(erros: FieldErrors<FormularioNovaGmud>): Etapa | undefined {
  const comErro = Object.keys(erros)
  return ETAPAS.find((etapa) => etapa.campos.some((campo) => comErro.includes(campo)))
}
